import React, { 
    createContext, useContext, useState, useMemo, 
    useReducer, useCallback, useRef
} from 'react';
import ReactGA from "react-ga4";
import dayjs from "dayjs";
import { cedulasReducer, initialStateCedulas } from '../reducers/cedulasReducers'; 
import { fetchStateMunicipalityWithCedula, identityCardsOfMissingPersons, fetchEstatusWithCedula } from 'Services/apiCedulas';
import { formatDate, hasInfoField, TAG_DATA as getTagData, verifyAge, verifyStature } from 'Utils/MissingPersonUtils';
import { message } from 'antd';

const CedulasPageContext = createContext(null);

const CedulasPageContextProvider = ({ children }) => {  
    const [cedulasData, dispatch] = useReducer(cedulasReducer, initialStateCedulas);

    const [cedulas, setCedulas] = useState();
    const [estatusWithCedulas, setEstatusWithCedulas] = useState([]);
    const [selectedEstatus, setSelectedEstatus] = useState("");
    const [stateMunicipalityWithCedulas, setStateMunicipalityWithCedulas] = useState({});
    const [selectedState, setSelectedState] = useState("");
    const [selectedMunicipality, setSelectedMunicipality] = useState("");
    const [searchedQR, setSearchedQR] = useState(false);
    const [searchedShare, setSearchedShare] = useState(false);

    const nameFilterRef = useRef(null);

    const getFilteredKeys = (obj) => Object.keys(obj ?? {}).filter((key) => key !== "null");
    
    const statesWithCedulas = useMemo(() => getFilteredKeys(stateMunicipalityWithCedulas), [stateMunicipalityWithCedulas]);
    const selectedStateID = useMemo(() => stateMunicipalityWithCedulas?.[selectedState]?.estado_id ?? null, [stateMunicipalityWithCedulas, selectedState]);
    const municipalitiesWithCedulas = useMemo(() => getFilteredKeys(stateMunicipalityWithCedulas?.[selectedState]?.municipios), [stateMunicipalityWithCedulas, selectedState]);
    const selectedMunicipalityID = useMemo(() => stateMunicipalityWithCedulas?.[selectedState]?.municipios?.[selectedMunicipality] ?? null, [stateMunicipalityWithCedulas, selectedState, selectedMunicipality]);
    
    const [filterPage, setfilterPage] = useState(1);

    const cedulaAnalyticsEvent = (action, label) => {
        ReactGA.event({
            category: 'Cedulas',
            action,
            label
        });
    };
    
    const handleFilterChange = useCallback((field) => (event) => {
        let value = event?.target?.value;
        if (field === 'totalPages') {
            value = event ?? 0;
        }

        if (field === 'disappearanceDateStart' ||
            field === 'disappearanceDateEnd'
        ) {
            const formattedDate = dayjs(event);
            value = formattedDate.isValid()
                ? formattedDate.format('YYYY-MM-DD')
                : null;
        }

        dispatch({ type: 'SET_CEDULA_DATA', field, value });
    }, []);
    
    const handleFiltersReset = () => {
        dispatch({ type: 'RESET_CEDULA_DATA' });
    };

    const handleLoadingCedulas = (field) => {
        dispatch({ type: 'LOADING', field });
    };

    const handleFinishedLoadingCedulas = (field) => {
        dispatch({ type: 'FINISH_LOADING', field });
    };

    const handleSearchedQR = (state) => {
        cedulaAnalyticsEvent(
            'Buscar por QR',
            'Búsqueda realizada por medio del QR de la cédula.'
        );
        setSearchedQR(state);
    };

    const handleSearchedShare = (state) => {
        cedulaAnalyticsEvent(
            'Buscar por compartir',
            'Búsqueda realizada por medio del URL compartido de la cédula.'
        );
        setSearchedShare(state);
    };

    const handleSelectedEstatus = (estatus) => setSelectedEstatus(estatus);

    const searchPeopleCedulas = useCallback((filterPage, quickSearch) => {
        handleLoadingCedulas('loadingSearch');
        const paramsSearch = quickSearch 
            ? {
                id_cedula_busqueda: quickSearch,
                page: filterPage || 1
            } : { 
                estado: selectedStateID,
                municipio: selectedMunicipalityID || "",
                condicion_localizacion: selectedEstatus || "",
                sexo: cedulasData.sex, 
                edad_momento_desaparicion: cedulasData.age, 
                nombre: nameFilterRef.current.value,
                page: filterPage || 1
            };
        
        const hasStartDate = Boolean(cedulasData.disappearanceDateStart);
        const hasEndDate = Boolean(cedulasData.disappearanceDateEnd);

        if (!quickSearch && hasStartDate && hasEndDate) {
            paramsSearch.fecha_desaparicion_inicio = cedulasData.disappearanceDateStart;
            paramsSearch.fecha_desaparicion_fin = cedulasData.disappearanceDateEnd;
        }

        if (!quickSearch && hasStartDate && !hasEndDate) {
            paramsSearch.fecha_desaparicion = cedulasData.disappearanceDateStart;
        }

        if (!quickSearch && !hasStartDate && hasEndDate) {
            paramsSearch.fecha_desaparicion = cedulasData.disappearanceDateEnd;
        }

        identityCardsOfMissingPersons(paramsSearch).then((response) => {
            let resultados = response.data.results;
            let totalCedulas = response.data.count;
            resultados.forEach((person) => {
                if (person.ruta_foto === null || person.ruta_foto?.includes('tiff')) {
                    person.ruta_foto = "./no_image.png";
                }
            });
            setCedulas(resultados);
            handleFilterChange('totalPages')(response.data.total_pages);
            if ( totalCedulas === 0 ) {
                return message.info("No se encontraron resultados con esos filtros.");
            }
            if ( filterPage === 1 ) {
                message.success(`Búsqueda con filtro completada exitosamente: ${totalCedulas} cédulas de búsqueda encontradas.`);
            }
        }).catch((error) => {
            console.error(error);
            message.error("Error al obtener información.");
        }).finally(() => {
            handleFinishedLoadingCedulas('loadingSearch');
        });
    }, [handleFilterChange, cedulasData.age, cedulasData.disappearanceDateStart, cedulasData.disappearanceDateEnd, cedulasData.sex, selectedMunicipalityID, selectedStateID, selectedEstatus])

    const handleStateMunicipalityWithCedulas = () => {
        if (
            stateMunicipalityWithCedulas &&
            Object.keys(stateMunicipalityWithCedulas).length > 0
        ) {
            return;
        }

        handleLoadingCedulas('loadingState');

        fetchStateMunicipalityWithCedula().then((states) => {
            const data = states.data ?? {};
            setStateMunicipalityWithCedulas(data);
            const estados = getFilteredKeys(data);
            setSelectedState(estados[0] ?? "");
            setSelectedMunicipality("");
        }).catch((error) => {
            console.error("fetchState: ", error);
        }).finally(() => {
            handleFinishedLoadingCedulas('loadingState');
        });
    };

    const handleEstatusWithCedulas = () => {
        if (estatusWithCedulas.length > 0 || cedulasData.loadingEstatusCedula) {
            return;
        }
        handleLoadingCedulas('loadingEstatusCedula');
        fetchEstatusWithCedula().then((response) => {
            const data = response.data?.results ?? response.data ?? [];
            setEstatusWithCedulas(data);
        }).catch((error) => {
            console.error("fetchEstatusWithCedula: ", error);
            message.error(
                "Error al obtener los estatus."
            );
        }).finally(() => {
            handleFinishedLoadingCedulas('loadingEstatusCedula');
        });
    };
    
    const handleSelectedState = (state) => setSelectedState(state)
    const handleSelectedMunicipality = (municipality) => setSelectedMunicipality(municipality)

    const cleanStateCedulasContext = () => {
        setStateMunicipalityWithCedulas({});
        setSelectedState("");
        setSelectedMunicipality(null);
        handleFiltersReset();
        setSelectedEstatus("");
    }

    const handlePageChange = useCallback((_event, page) => {
        setfilterPage(page);
        searchPeopleCedulas(page);
        
        let element = document.getElementById("container");
        if (element) element.scrollIntoView();
    }, [searchPeopleCedulas]);

    const forContextData = {
        cleanStateCedulasContext,
        getTagData, hasInfoField, verifyAge, verifyStature, formatDate,
        cedulaAnalyticsEvent, 
        nameFilterRef,
        cedulasData, handleFilterChange, handleFiltersReset, handleLoadingCedulas, handleFinishedLoadingCedulas,
        cedulas, searchPeopleCedulas, 
        searchedQR, handleSearchedQR,
        searchedShare, handleSearchedShare,
        stateMunicipalityWithCedulas, handleStateMunicipalityWithCedulas,
        selectedStateID, statesWithCedulas, selectedState, handleSelectedState,
        selectedMunicipalityID, municipalitiesWithCedulas, selectedMunicipality, handleSelectedMunicipality,
        estatusWithCedulas, handleEstatusWithCedulas, selectedEstatus, handleSelectedEstatus,
        filterPage, setfilterPage, handlePageChange,
    }

    return (
        <CedulasPageContext.Provider value={forContextData} children={children} />
    )

}

CedulasPageContext.displayName = 'CedulasPageContext';

const useCedulasContext = () => {
    const context = useContext(CedulasPageContext);
    if (!context) {
        throw new Error('useCedulasContext debe usarse dentro de un CedulasPageContextProvider');
    }
    return context;
};

export { CedulasPageContextProvider, CedulasPageContext, useCedulasContext, formatDate };
